import React, { useEffect, useState } from 'react';
import api from '../utils/api';
import IconButton from '../components/IconButton';
import { useToast } from '../components/Toast';
import { relativeTime } from '../utils/relativeTime';

/**
 * UsersPage — manager-only user management.
 * Promote/demote between Viewer and User, reset passwords, delete users.
 * Managers are shown read-only (the API rejects modifying them).
 */
export default function UsersPage() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [resetResult, setResetResult] = useState(null);
  const { push: toast } = useToast();

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const resp = await api.get('/api/users');
      setUsers(resp.data);
      setError('');
    } catch {
      setError('Failed to load users.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchUsers(); }, []);

  const changeRole = async (id, role) => {
    try {
      const resp = await api.patch(`/api/users/${id}/role`, { role });
      setUsers((prev) => prev.map((u) => (u._id === id ? { ...u, role: resp.data.role } : u)));
      toast({ kind: 'success', message: 'Role updated.' });
    } catch (err) {
      toast({ kind: 'error', message: err.response?.data?.message || 'Failed to update role.' });
    }
  };

  const resetPassword = async (id, username) => {
    if (!window.confirm(`Reset password for ${username}?`)) return;
    try {
      const resp = await api.post(`/api/users/${id}/reset-password`);
      const pw = resp.data.password;
      navigator.clipboard.writeText(pw).catch(() => {});
      setResetResult({ username, password: pw });
    } catch (err) {
      toast({ kind: 'error', message: err.response?.data?.message || 'Failed to reset password.' });
    }
  };

  const copyResetPassword = () => {
    if (!resetResult) return;
    navigator.clipboard.writeText(resetResult.password)
      .then(() => toast({ kind: 'success', message: 'Password copied.' }))
      .catch(() => toast({ kind: 'error', message: 'Copy failed.' }));
  };

  const removeUser = async (id, username) => {
    if (!window.confirm(`Delete user ${username}? This cannot be undone.`)) return;
    try {
      await api.delete(`/api/users/${id}`);
      toast({ kind: 'success', message: 'User deleted.' });
      fetchUsers();
    } catch (err) {
      toast({ kind: 'error', message: err.response?.data?.message || 'Failed to delete user.' });
    }
  };

  const stats = {
    managers: users.filter((u) => u.role === 'manager').length,
    users: users.filter((u) => u.role === 'user').length,
    viewers: users.filter((u) => u.role === 'viewer').length
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-[22px] font-bold tracking-tight">Users</h1>
          <div className="text-xs text-surface-muted">
            {stats.managers} manager · {stats.users} user · {stats.viewers} viewer
          </div>
        </div>
      </div>

      {resetResult && (
        <div className="mb-4 bg-surface-panel border border-accent px-[14px] py-3 rounded-[2px] flex items-center justify-between gap-3">
          <div className="text-xs text-surface-text">
            Temp password for <span className="text-accent font-semibold">{resetResult.username}</span>:
            <span className="font-mono ml-2 select-all">{resetResult.password}</span>
            <span className="text-surface-muted ml-2">(copied to clipboard — shown once)</span>
          </div>
          <div className="flex gap-1">
            <IconButton icon="📋" onClick={copyResetPassword} title="Copy password" />
            <IconButton icon="✕" onClick={() => setResetResult(null)} title="Dismiss" />
          </div>
        </div>
      )}

      {error && <div className="text-steam-danger text-sm mb-3">{error}</div>}
      {loading && <div className="text-surface-muted text-sm mb-3">Loading…</div>}

      <div className="bg-surface-panel border border-surface-border">
        <div className="grid grid-cols-[1fr_140px_1fr_120px] gap-3 px-[14px] py-[10px] bg-surface-header border-b border-surface-border text-[10px] font-bold uppercase tracking-[0.08em] text-surface-muted">
          <div>Username</div>
          <div>Role</div>
          <div>Created</div>
          <div className="text-right">Actions</div>
        </div>

        {users.length === 0 && !loading && (
          <div className="p-10 text-center text-surface-muted text-sm">No users yet.</div>
        )}

        {users.map((u) => {
          const isManager = u.role === 'manager';
          const created = relativeTime(u.createdAt);
          return (
            <div key={u._id} className="grid grid-cols-[1fr_140px_1fr_120px] gap-3 px-[14px] py-[9px] border-b border-surface-base items-center text-xs">
              <div className="text-surface-text font-semibold">{u.username}</div>
              <div>
                {isManager ? (
                  <span
                    className="text-accent font-semibold uppercase text-[10px] tracking-wider"
                    title="Managers can't be modified"
                  >Manager</span>
                ) : (
                  <select
                    aria-label={`Role for ${u.username}`}
                    value={u.role}
                    onChange={(e) => changeRole(u._id, e.target.value)}
                    className="bg-surface-base text-surface-text border border-surface-border text-xs px-2 py-1 rounded-[2px] focus:outline-none focus:ring-2 focus:ring-accent"
                  >
                    <option value="viewer">Viewer</option>
                    <option value="user">User</option>
                  </select>
                )}
              </div>
              <div className="text-surface-muted" title={created.absolute}>{created.relative}</div>
              <div className="flex gap-1 justify-end">
                {isManager ? (
                  <span className="text-[10px] text-surface-muted">—</span>
                ) : (
                  <>
                    <IconButton icon="🔑" onClick={() => resetPassword(u._id, u.username)} title="Reset password" />
                    <IconButton icon="✕" color="danger" onClick={() => removeUser(u._id, u.username)} title="Delete user" />
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
